/*
===============================================================================================================
                                 Copyright 2023-2024, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, {
  useState, useEffect, useContext, useRef,
} from 'react';
import PropTypes from 'prop-types';
import ChevronDown from '@/public/icons/chevron-down.svg';
import { ThemeContext } from '@/contexts/theme-context';
import { LocaleContext } from '@/contexts/locale-context';
import InlineEditableText from '@/components/inline-editable-text';
import LoadingArc from '@/components/loading-arc';

/**
 *  Editabe Dropdown Component is designed to mimic the look and feel of a dropdown but is editable.
 * @param {string} inputSelectorId - DOM id, used to identify this element
 * @param {string} title - Title default text, displayed above the dropdown field
 * @param {Array} items - in the format { title: 'Item 1', value: 'value1' },
 * @param {number} inputWidth - CSS Percentage width. Defaults to 100
 * @param {number} inputHeight - CSS pixel width. Defaults to 48px
 * @param {func} onChangeTitle - Called when the title is saved
 * @param {func} onChangeInput - Called when the placeholder is saved
 * @param {func} onChangeDropdown - Called when the dropdown is saved
 * @param {string} placeholder - Placeholder default text
 * @param {string} defaultResetValueTitle - A text string to display instead of an empty title when resetting the value
 * @param {string} defaultResetValuePlaceholder - A text string to display instead of an empty title when resetting the value
 * @param {boolean} showAsRequired -  Whether or not the title should be shown as a required field
 * @param {number} maxDropdownItemslength - Maximum number of dropdown items. Defaults to 10
 * @param {function} onChange - Optional function called when the input value changes
 * @param {shape} titleRules - The validation rules object of the title
 * @param {shape} placeholderRules - The validation rules object of the placeholder
 * @param {shape} titleProps - The extra props of the title field in config
 * @param {shape} placeholderProps - The extra props of the placeholder field in config
 * @param {shape} dropdownProps - The extra props of the dropdown field in config
 * @param {string} error - Optional. Display this error on component load
 * @param {boolean} disabled
 * @param {boolean} loading
 */

function EditableDropdown({
  inputSelectorId,
  title,
  items,
  inputWidth,
  inputHeight,
  onChangeTitle,
  onChangeInput,
  onChangeDropdown,
  maxDropdownItemslength,
  showAsRequired,
  placeholder,
  primaryColor,
  disabled,
  loading,
  titleRules,
  placeholderRules,
  defaultResetValueTitle,
  defaultResetValuePlaceholder,
  titleProps,
  placeholderProps,
  dropdownProps,
  showPlaceholder,
  dropdownItemRules,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { textColor, alternateTextColor, primaryColor: themePrimaryColor } = useContext(ThemeContext);
  const { textDirection, translate } = useContext(LocaleContext);
  const [wrapperWidth, setWrapperWidth] = useState(0);
  const [dropdownItems, setDropdownItems] = useState(items);

  useEffect(() => {
    setDropdownItems(items);
  }, [items]);

  useEffect(() => {
    if (JSON.stringify(items) !== JSON.stringify(dropdownItems)) {
      onChangeDropdown(dropdownItems);
    }
  }, [dropdownItems]);

  const inputWrapper = useRef();

  useEffect(() => {
    setWrapperWidth(inputWrapper.current.offsetWidth);
  }, []);

  const onKeyDown = (event, value, func) => {
    if (!disabled && !loading && !dropdownProps.readOnly) {
      if (event.key === 'Enter' && value) {
        func(value);
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        func(true);
      }
    }
  };

  const handleDropdownClick = () => {
    if (!disabled && !loading && !dropdownProps.readOnly) {
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    }
  };

  const handleEdit = (value, index = 0) => {
    setDropdownItems((prevState) => {
      const newItems = [...prevState];
      newItems[index].value = value;
      newItems[index].title = value;
      return newItems;
    });
  };

  const handleDelete = (index = 0) => {
    setDropdownItems((prevState) => {
      const newItems = [...prevState];
      newItems.splice(index, 1);
      return newItems;
    });
  };

  const handleAdd = () => {
    if (dropdownItems.length < maxDropdownItemslength) {
      setDropdownItems([...dropdownItems, { title: translate('customisation.dropdownItem'), value: translate('customisation.dropdownItem') }]);
    }
    return null;
  };

  const onKeyDownItem = (event) => {
    if (event.key === 'Enter') {
      handleAdd();
    }
  };

  return (
    <>
      <div className="dropdown">
        {(title || defaultResetValueTitle) && (
          <div className="title">
            <InlineEditableText
              ariaLabel={titleProps.ariaLabel}
              inputSelectorId={`${inputSelectorId}-title`}
              defaultValue={title}
              defaultResetValue={defaultResetValueTitle}
              externalWrapperWidth={wrapperWidth}
              onChange={onChangeTitle}
              showAsRequired={showAsRequired}
              readOnly={titleProps.readOnly}
              textAreaMode={titleProps.textAreaMode}
              maxLength={titleRules.maxLength}
              tooltipConfig={titleProps.tooltipParams}
              validationRules={{
                onChange: titleRules.onChange,
                onBlur: titleRules.onBlurValidationRules,
                onSubmit: titleRules.onSubmit,
                requiredError: translate(titleRules.requiredError),
                onChangeErrors: titleRules.onChangeErrors,
                onBlurErrors: titleRules.onBlurValidationErrors,
                submitErrors: titleRules.submitErrors,
                tooLongError: translate(titleRules.tooLongError),
              }}
            />
          </div>
        )}
        <div
          id={inputSelectorId}
          ref={inputWrapper}
          className="dropdown-selection"
        >
          {showPlaceholder && (
            <span id={`${inputSelectorId}-selected-value`} className="dropdown-selection-text">
              <InlineEditableText
                ariaLabel={placeholderProps.ariaLabel}
                inputSelectorId={`${inputSelectorId}-placeholder`}
                defaultValue={placeholder}
                defaultResetValue={defaultResetValuePlaceholder}
                onChange={onChangeInput}
                fontWeight="var(--font-weight-body)"
                dropdownMode
                textDisabled
                externalWrapperWidth={wrapperWidth}
                readOnly={placeholderProps.readOnly}
                maxLength={placeholderRules.maxLength}
                validationRules={{
                  onChange: placeholderRules.onChange,
                  onBlur: placeholderRules.onBlurValidationRules,
                  onSubmit: placeholderRules.onSubmit,
                  requiredError: translate(placeholderRules.requiredError),
                  onChangeErrors: placeholderRules.onChangeErrors,
                  onBlurErrors: placeholderRules.onBlurValidationErrors,
                  submitErrors: placeholderRules.submitErrors,
                  tooLongError: translate(placeholderRules.tooLongError),
                }}
              />
              {loading && <LoadingArc inline />}
            </span>
          )}
          <div
            className={`dropdown-arrow${textDirection === 'rtl' ? ' rtl' : ''}`}
            onClick={handleDropdownClick}
            onKeyDown={(event) => onKeyDown(event, !isOpen, setIsOpen)}
            role="button"
            tabIndex={dropdownProps.readOnly ? -1 : 0}
          >
            <div className={`arrow-down ${isOpen ? 'open' : ''}`}><ChevronDown /></div>
          </div>
        </div>
        {isOpen && (
          <ul
            id={`${inputSelectorId}-dropdown-list`}
            className={`dropdown-list${isOpen ? ' open' : ''}`}
          >
            {dropdownItems?.map((item, idx) => (
              <li
              // eslint-disable-next-line react/no-array-index-key
                key={`dropdown-item-${idx}`}
                className="dropdown-item"
              >
                <InlineEditableText
                  ariaLabel={dropdownProps.ariaLabel}
                  inputSelectorId={`${inputSelectorId}-item-${idx}`}
                  defaultValue={item.title}
                  externalWrapperWidth={wrapperWidth}
                  index={idx}
                  dropdownMode
                  validationRules={dropdownItemRules}
                  onChange={handleEdit}
                  deleteOnChange={handleDelete}
                />
              </li>
            ))}
            <li>
              <div
                role="button"
                title="add more"
                className="add-more"
                id="dropdown-item-add-more-button"
                dir={textDirection}
                onKeyDown={(event) => onKeyDownItem(event)}
                tabIndex={0}
                onClick={handleAdd}
                disabled={maxDropdownItemslength && dropdownItems.length >= maxDropdownItemslength}
              >
                {translate('customisation.addMore')}
              </div>
            </li>
          </ul>
        )}
      </div>
      <style jsx>
        {`

          .arrow-down {
            transition: transform .24s;
          }

          .arrow-down.open {
            transform: rotate(180deg);
          }

          .dropdown {
            color: ${textColor};
            cursor: ${
            // eslint-disable-next-line no-nested-ternary
            disabled ? 'not-allowed' : loading ? 'progress' : 'pointer'
            };
            display: flex;
            flex-direction: column;
            font-size: var(--font-size-body);
            font-weight: var(--font-weight-body);
            position: relative;
            width: ${inputWidth}%;
            min-height: ${inputHeight}px;
          }

          .title {
            color: var(--color-primary-dark);
            display: block;
            font-size: var(--font-size-H2);
            font-weight: var(--font-weight-H2);
          }

          .dropdown-selection {
            align-items: center;
            background-color: var(${disabled ? '--color-light-grey' : '--color-white'});
            border-radius: var(--border-radius);
            border: 1px solid  var(${primaryColor});
            color: var(--color-primary-dark);
            font-size: var(--font-size-body);
            font-weight: var(--font-weight-body);
            display: flex;
            height: ${inputHeight}px;
            padding: 0 14px;
            position: relative;
          }


          .dropdown-selection-text {
            max-width: 90%;
            padding-right: ${textDirection === 'ltr' ? '14px' : 'initial'};
            padding-left: ${textDirection === 'rtl' ? '14px' : 'initial'};
            text-overflow: ellipsis;
            white-space: nowrap;
            user-select: none;
            display: flex;
            align-items: center;
            gap: var(--spacing);
          }

          .dropdown-selection-text.empty {
            color: var(${primaryColor});
            font-size: var(--font-size-address);
            font-weight: var(--font-weight-body);
          }

          .dropdown-arrow {
            align-items: center;
            border-radius: var(--border-radius);
            border: solid  var(${primaryColor});
            border-width: 0 0 0 1px;
            display: flex;
            height: ${inputHeight}px;
            justify-content: center;
            margin-left: auto;
            margin-right: -15px;
            text-align: center;
            width: 48px;
          }



          .dropdown-arrow.rtl {
            border-width: 0 1px 0 0;
            margin-right: auto;
            margin-left: -15px;
          }

          .dropdown-list { 
            animation: fadeIn 0.2s ease-in-out;
            align-items: flex-start;
            background-color: var(--color-white);
            border-radius: var(--border-radius);
            border: 1px solid var(--color-line-grey);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
            display: block;
            padding: 0;
            list-style: none;
            flex-direction: column;
            margin-top: 100px;
            max-height: 240px;
            overflow-y: auto;
            position: absolute;
            scroll-behavior: smooth;
            width: 100%;
            z-index: 100;
          }

          .dropdown-item {
            align-items: center;
            border-bottom: 1px solid var(--color-line-grey);
            display: flex;
            line-height: 24px;
            min-height: ${inputHeight}px;
            overflow: hidden;
            padding: 0px 30px 0px 15px;
            word-break: break-word;
            width: 100%;
          }


          .dropdown-item:hover {
            background-color: var(--color-off-white);
            color: ${alternateTextColor};
            outline: none;
          }

          .add-more {
            align-items: center;
            display: flex;
            line-height: 24px;
            min-height: ${inputHeight}px;
            overflow: hidden;
            padding: 0px 30px 0px 20px;
            word-break: break-word;
            width: 100%;
          }

          .add-more:hover {
            background-color: ${themePrimaryColor || 'var(--color-hover-dark-blue)'} ;
            color: var(--color-white);
            outline: none;
          }

          .add-more:disabled, .add-more[disabled] {
            background-color: var(--color-light-grey);
            color: var(--color-mid-grey);
            cursor: not-allowed;
          }


          .error {
            margin-top: 3px;
            min-height: 20px;
            visibility: hidden;
            white-space: break-spaces;
          }

          .error.visible {
            color: var(--color-error-red);
            font-size: var(--font-size-error);
            margin-top: 3px;
            text-align: left;
            visibility: visible;
          }

          .error.visible + .dropdown-list.open {
            margin-top: -18px;
          }

          .error.rtl {
            text-align: right;
          }

          .error.hidden {
            display: none;
          }

          @keyframes fadeIn {
            0% {
              opacity: 0;
              transform: translateY(-30px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @media only screen and (max-width: 768px) {
            .dropdown {
              font-size: var(--font-size-body-mobile);
              font-weight: var(--font-weight-body-mobile);
              width: 100%;
            }

            .title {
              font-size: var(--font-size-H2-mobile);
              font-weight: var(--font-weight-H2);
            }

            .error.visible {
              font-size: var(--font-size-error-mobile);
            }
          }
        `}
      </style>
    </>
  );
}

EditableDropdown.defaultProps = {
  title: 'Please enter title',
  showAsRequired: false,
  inputSelectorId: null,
  placeholder: 'Please enter placeholder',
  inputWidth: 100,
  inputHeight: 48,
  maxDropdownItemslength: 10,
  onChangeTitle: () => {},
  onChangeInput: () => {},
  onChangeDropdown: () => {},
  primaryColor: '--color-mid-grey',
  items: [],
  disabled: false,
  loading: false,
  defaultResetValueTitle: null,
  defaultResetValuePlaceholder: null,
  titleProps: {
    readOnly: false,
    textAreaMode: false,
    tooltipParams: {},
  },
  placeholderProps: {
    readOnly: false,
    textAreaMode: false,
    minHeight: null,
  },
  dropdownProps: {
    readOnly: false,
  },
  titleRules: {
    onChange: null,
    onBlurValidationRules: null,
    onSubmit: null,
    requiredError: null,
    onChangeErrors: null,
    onBlurValidationErrors: null,
    submitErrors: null,
    minLength: null,
    maxLength: null,
    tooShortError: null,
    tooLongError: null,
  },
  placeholderRules: {
    onChange: null,
    onBlurValidationRules: null,
    onSubmit: null,
    requiredError: null,
    onChangeErrors: null,
    onBlurValidationErrors: null,
    submitErrors: null,
    minLength: null,
    maxLength: null,
    tooShortError: null,
    tooLongError: null,
  },
  dropdownItemRules: {
    onChange: null,
    onBlurValidationRules: null,
    onSubmit: null,
    requiredError: null,
    onChangeErrors: null,
    onBlurValidationErrors: null,
    submitErrors: null,
    minLength: null,
    maxLength: null,
    tooShortError: null,
    tooLongError: null,
  },
  showPlaceholder: true,
};

EditableDropdown.propTypes = {
  title: PropTypes.string,
  showAsRequired: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      value: PropTypes.string,
    }),
  ),
  placeholder: PropTypes.string,
  maxDropdownItemslength: PropTypes.number,
  inputSelectorId: PropTypes.string,
  inputWidth: PropTypes.number,
  inputHeight: PropTypes.number,
  onChangeTitle: PropTypes.func,
  onChangeInput: PropTypes.func,
  onChangeDropdown: PropTypes.func,
  primaryColor: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  defaultResetValueTitle: PropTypes.string,
  defaultResetValuePlaceholder: PropTypes.string,
  titleProps: PropTypes.shape({
    readOnly: PropTypes.bool,
    textAreaMode: PropTypes.bool,
    ariaLabel: PropTypes.string,
    tooltipParams: PropTypes.shape({
      enabled: PropTypes.bool,
      defaultResetValue: PropTypes.string,
      onSubmitValidationRules: PropTypes.arrayOf(PropTypes.string),
      onChangeValidationRules: PropTypes.arrayOf(PropTypes.string),
      maxLength: PropTypes.number,
      tooLongError: PropTypes.string,
    }),
  }),
  placeholderProps: PropTypes.shape({
    ariaLabel: PropTypes.string,
    readOnly: PropTypes.bool,
    textAreaMode: PropTypes.bool,
    minHeight: PropTypes.string,
  }),
  dropdownProps: PropTypes.shape({
    ariaLabel: PropTypes.string,
    readOnly: PropTypes.bool,
  }),
  titleRules: PropTypes.shape({
    onSubmit: PropTypes.arrayOf(PropTypes.string),
    onBlurValidationRules: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.arrayOf(PropTypes.string),
    requiredError: PropTypes.string,
    submitErrors: PropTypes.arrayOf(PropTypes.string),
    onChangeErrors: PropTypes.arrayOf(PropTypes.string),
    onBlurValidationErrors: PropTypes.arrayOf(PropTypes.string),
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    tooShortError: PropTypes.string,
    tooLongError: PropTypes.string,
  }),
  placeholderRules: PropTypes.shape({
    onSubmit: PropTypes.arrayOf(PropTypes.string),
    onBlurValidationRules: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.arrayOf(PropTypes.string),
    requiredError: PropTypes.string,
    submitErrors: PropTypes.arrayOf(PropTypes.string),
    onChangeErrors: PropTypes.arrayOf(PropTypes.string),
    onBlurValidationErrors: PropTypes.arrayOf(PropTypes.string),
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    tooShortError: PropTypes.string,
    tooLongError: PropTypes.string,
  }),
  dropdownItemRules: PropTypes.shape({
    onSubmit: PropTypes.arrayOf(PropTypes.string),
    onBlurValidationRules: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.arrayOf(PropTypes.string),
    requiredError: PropTypes.string,
    submitErrors: PropTypes.arrayOf(PropTypes.string),
    onChangeErrors: PropTypes.arrayOf(PropTypes.string),
    onBlurValidationErrors: PropTypes.arrayOf(PropTypes.string),
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    tooShortError: PropTypes.string,
    tooLongError: PropTypes.string,
  }),
  showPlaceholder: PropTypes.bool,
};

export default EditableDropdown;
